/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { memo, useContext, useEffect, useMemo, useState } from 'react'
import numeral from 'numeral';

import { Box, Grid, IconButton, List, ListItem, Tooltip, Typography } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import CheckIcon from '@mui/icons-material/Check';
import { CoverageItem } from "./styles";
import { AdditionalSmallItemIcon } from '../Accordion';
import TogglePlans from './TogglePlans';

import { CoverageDetailsParameter } from 'shared/api/assurant/quoteResponseTypes';
import { FormDataContext } from 'app/providers/formValues/lib/FormDataContext';
import EditableDate from './EditableDate';
import EditableBelongings from './EditableBelongings';
import EditableCoverage from './EditableCoverage';
import EditableDeductible from './EditableDeductible';
import EditableLiability from './EditableLiability';
import { StyledButton } from '../Button/styles';
import { getTooltipDescription, tooltipDescriptions } from './tooltipDescriptions';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import { useJourneyResultFormQuery } from '../../../entities/ApplicationForm/hooks/queries';

const coverageDesc: Record<string, string> = {
	'hurricaneDeductible': 'Hurricane Deductible',
	'floodIncluded': 'Flood Coverage',
	'petDamage': 'Pet Damage',
	'waterDamageLiability': 'Water Damage Liability',
	'liabilityAmount': 'Liability Amount',
	'personalPropertyAmount': 'Your Belongings',
	'replacementCost': 'Replacement Cost',
	'waterBackup': 'Water Sewer Backup',
	'earthquake': 'Earthquake',
	'deductible': 'Deductible',
	'identityFraud': 'Fraud Expense Protection',
}

const yesNoText: Record<string, string> = {
	'N': 'No',
	'Y': 'Added',
}

// Define custom order
const coverageOrder: Record<string, number> = {
	'liabilityAmount': 1,
	'personalPropertyAmount': 2,
	'deductible': 3,
	'replacementCost': 4,
	'floodIncluded': 5,
	'waterBackup': 6,
	'petDamage': 7,
	'earthquake': 8,
	'identityFraud': 9,
	'waterDamageLiability': 10,
	'hurricaneDeductible': 11,
}

const includedArray = ['replacementCost', 'floodIncluded']
const editableCoverages = ['hurricaneDeductible', 'earthquake', 'waterBackup', 'identityFraud', 'floodIncluded', 'petDamage', 'waterDamageLiability']

interface CoverageInfoProps {
	loading: boolean;
	onSubmit: () => void;
}

const CoverageInfo = ({ onSubmit, loading }: CoverageInfoProps) => {
	const { query: queryState, plan, protections, defaultQuery } = useContext(FormDataContext);
	const journeyResult = useJourneyResultFormQuery();
	const coverages = queryState?.coverageDetails.parameters
	const state = queryState?.policyHolderDetails.insured.address.state
	const [isDateInPast, setIsDateInPast] = useState(false);
	const coverageData: string = useMemo(() => {
		return queryState?.transactionDetails.policyEffectiveDate ?? ''
	}, [queryState]);


	useEffect(() => {
		if(journeyResult) {
			const isDateInPast = dayjs.utc(journeyResult.quote_result.transactionDetails.policyEffectiveDate).isBefore(dayjs.utc(), 'day');
			if (isDateInPast) {
				setIsDateInPast(true)
			}
		}
	}, [journeyResult])

	const hasCoverages = useMemo(() => {
		if (!defaultQuery) return {
			hasIdFraud: 'N',
			hasWaterBackup: 'Y',
			hasEarthquake: 'N',
			hasReplacementCost: 'N',
			hasPetDamage: 'N'
		}
		return defaultQuery?.coveragesCue.parameters.reduce<Record<string, string | number[] | null>>((accum, item) => {
			accum[item.name] = item.value
			return accum
		}, {})
	}, [defaultQuery]);

	// Filter and sort coverages
	const orderedCoverages = useMemo(() => {
		if (!coverages) return [];

		return coverages
			.filter((item) => {
				// Filter logic
				if (item.name === 'hurricaneDeductible'
					|| item.name === 'earthquake'
					|| item.name === 'waterBackup'
					|| item.name === 'identityFraud'
					|| item.name === 'floodIncluded'
					|| item.name === 'petDamage'
					|| item.name === 'waterDamageLiability'
				) {
					if (item.name === 'earthquake' && state === 'CA') return true;
					if (item.name === 'waterBackup' && hasCoverages?.hasWaterBackup === 'Y') return true;
					if (item.name === 'floodIncluded' && protections.floodIncluded) return true;
					if (item.name === 'waterDamageLiability' && state === 'NC') return true;
					if (item.name === 'identityFraud' && hasCoverages?.hasIdFraud === 'Y') return true;
					if (item.name === 'petDamage' && hasCoverages?.hasPetDamage === 'Y') return true;
					if (item.name === 'hurricaneDeductible' && state === 'FL') return true;
					return false;
				} else {
					return coverageDesc[item.name] !== undefined;
				}
			})
			.sort((a: { name: string | number; }, b: { name: string | number; }) => {
				const orderA = coverageOrder[a.name] ?? 999;
				const orderB = coverageOrder[b.name] ?? 999;
				return orderA - orderB;
			});
	}, [coverages, state, hasCoverages, protections]);

	const renderItem = (item: CoverageDetailsParameter) => {
		const number = numeral(item.value)
		numeral.zeroFormat(item.value ?? '');
		numeral.nullFormat(item.value ?? '');
		const currency = number.format('$0,0[.]00')
		return (
			<ListItem sx={{ justifyContent: 'space-between', p: '2px 0' }} key={item.name}>
				<Box display="flex" justifyContent="flex-start" alignItems="center" >
					<Typography>{coverageDesc[item.name]}</Typography>
					<Tooltip title={getTooltipDescription(item.name as keyof typeof tooltipDescriptions, state)} placement="top" arrow>
						<IconButton sx={{ fontSize: '20px', color: 'secondary.dark'}}>
							<InfoIcon fontSize="inherit"/>
						</IconButton>
					</Tooltip>
				</Box>
				{includedArray.includes(item.name) && (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<AdditionalSmallItemIcon sx={{ backgroundColor: "primary.light" }}>
							<CheckIcon/>
						</AdditionalSmallItemIcon>
						<Typography fontWeight={800} textAlign="right">Included</Typography>
					</div>
				)}
				{!includedArray.includes(item.name) && item.value && (
					<>
						{item.name === 'liabilityAmount' ? (
							<EditableLiability/>
						) : item.name === 'personalPropertyAmount' ? (
							<EditableBelongings/>
						) : item.name === 'deductible' ? (
							<EditableDeductible/>
						) : editableCoverages.includes(item.name) ? (
							<EditableCoverage coverageName={item.name}/>
						) : (
							<Typography fontWeight={700}
							            textAlign="right">{yesNoText[item.value] || currency}</Typography>
						)}
					</>
				)}
			</ListItem>
		)
	};
	return (
		<>
			<CoverageItem>
				<Box sx={{ p: 3 }}>
					<Typography variant={"h4"} sx={{ mb: 1 }}>Coverage Estimator</Typography>
					<Typography variant={"subtitle2"}>Renters Insurance Summary</Typography>
					<List>
						<ListItem sx={{ justifyContent: 'space-between', p: '10px 0' }}>
							{coverageData && <EditableDate coverageData={coverageData} isDateInPast={isDateInPast}/>}
						</ListItem>
						<ListItem sx={{ justifyContent: 'space-between', p: '10px 0' }}>
							<Box display="flex" flexDirection="column">
								<Typography>Today's cost</Typography>
								<Typography fontStyle="italic">Downpayment</Typography>
							</Box>
							<Typography fontWeight={800} textAlign="right">
								{plan.downPaymentAmount && numeral(plan.downPaymentAmount).format('$0,0[.]00')}
							</Typography>
						</ListItem>
						{orderedCoverages.map((item) => renderItem(item))}
					</List>
				</Box>
			</CoverageItem>
		</>
	)
}

export default memo(CoverageInfo)
